import FeatureFormat from 'ol/format/feature'
import GeoJSONFormat from 'ol/format/geojson'
import PolylineFormat from 'ol/format/polyline'
import FormatType from 'ol/format/formattype'
import Feature from 'ol/feature'
import proj from 'ol/proj'

export default class GlonassJSON extends FeatureFormat {
    static DEFAULT_DATA_GEOMETRY_NAME = 'geometry'
    static DEFAULT_DATA_PRIMARY_KEY_NAME = 'id'
    static PROJECTION = 'EPSG:4326'

    constructor (options = {}) {
        super(options)
        /**
         * @inheritDoc
         */
        this.defaultDataProjection_ = this.defaultDataProjection = proj.get(
            options.defaultDataProjection != null
                ? options.defaultDataProjection
                : 'EPSG:4326'
        )

        /**
         * Name of the geometry attribute for features.
         * @type {string|undefined}
         * @private
         */
        this.geometryName_ = options.geometryName
        /**
         * @type {ol.format.Feature}
         * @protected
         */
        this.geometryFormat_ = options.geometryFormat != null ? options.geometryFormat : new GeoJSONFormat({
            defaultDataProjection: this.defaultDataProjection_,
            geometryName: this.geometryName_,
        })
        /**
         * @type {string}
         * @protected
         */
        this.dataGeometryName_ = options.dataGeometryName != null ? options.dataGeometryName : GlonassJSON.DEFAULT_DATA_GEOMETRY_NAME
        /**
         * @type {string}
         * @protected
         */
        this.dataPrimaryKeyName_ = options.dataPrimaryKeyName != null ? options.dataPrimaryKeyName : GlonassJSON.DEFAULT_DATA_PRIMARY_KEY_NAME
    }

    getReadOptions (source, opt_options = {}) {
        var options
        if (opt_options != null) {
            options = {
                dataProjection: opt_options.dataProjection != null
                    ? opt_options.dataProjection
                    : this.readProjection(source),
                featureProjection: opt_options.featureProjection,
            }
        }
        return this.adaptOptions(options)
    }

    adaptOptions (options = {}) {
        let updatedOptions
        if (options != null) {
            updatedOptions = {
                featureProjection: options.featureProjection,
                dataProjection: options.dataProjection != null
                    ? options.dataProjection
                    : this.defaultDataProjection,
                rightHanded: options.rightHanded,
            }
        }
        return updatedOptions
    }

    getObject_ (source) {
        if (typeof source === 'object') {
            return source
        } else if (typeof source === 'string') {
            try {
                let object = JSON.parse(source)
                return typeof object !== 'undefined' ? object : null
            } catch (e) {
                throw new Error('invalid json object: ' + e)
            }
        } else {
            throw new Error('invalid json object')
        }
    }

    getType () {
        return FormatType.JSON
    }

    readFeature (source, opt_options = {}) {
        return this.readFeatureFromObject(this.getObject_(source), this.getReadOptions(source, opt_options))
    }

    readFeatures (source, opt_options = {}) {
        return this.readFeaturesFromObject(this.getObject_(source), this.getReadOptions(source, opt_options))
    }

    readFeatureFromObject (object, opt_options = {}) {
        object || (object = {})
        opt_options || (opt_options = {})

        if (object.item) {
            object = object.item
        }

        opt_options.format || (opt_options.format = this.geometryFormat_)

        if (typeof object !== 'object') {
            throw 'wrong source object'
        }

        let geometry = this.readGeometry(object[this.dataGeometryName_], opt_options)
        let feature = new Feature()

        if (this.geometryName_) {
            feature.setGeometryName(this.geometryName_)
        }

        feature.setGeometry(geometry)

        if (object[this.dataPrimaryKeyName_]) {
            feature.setId(object[this.dataPrimaryKeyName_])
        }

        if (feature.getGeometryName() === this.dataGeometryName_) {
            delete object[this.dataGeometryName_]
        }

        feature.setProperties(object)

        return feature
    }

    readFeaturesFromObject (object, opt_options = {}) {
        object || (object = {})

        if (object.data) {
            object = object.data
        } else if (Array.isArray(object)) {
            object = { items: object }
        }

        if (object.item) {
            return [this.readFeatureFromObject(object, opt_options)]
        } else if (object.items) {
            let features = [],
                keys = Object.keys(object.items),
                i

            while (i = keys.shift()) {
                features.push(this.readFeatureFromObject(object.items[i], opt_options))
            }

            return features
        } else {
            throw new Error('wrong source object')
        }
    }

    readGeometry (source, opt_options = {}) {
        return this.readGeometryFromObject(this.getObject_(source), this.getReadOptions(source, opt_options))
    }

    readGeometry_ (object, opt_options = {}) {
        opt_options || (opt_options = {})

        if (object == null) {
            return null
        }

        if (opt_options.format == null) {
            throw new Error('undefined geometry format')
        }

        //var format = new ol.format.encodeMultiPointGeometry_().EncodedPolyline({geometryType:"linestring"});
        let format = new PolylineFormat({
            //factor: 1e6
        })

		let recurseDecode = function (object) {
			if (object.type === "GeometryCollection") {
				for (let i = 0; i < object.geometries.length; i++) {
					object.geometries[i] = recurseDecode(object.geometries[i])
				}
			} else if (object.type === 'Polygon') {
				for (let i = 0; i < object.coordinates.length; i++) {
					if (typeof object.coordinates[i] === 'string') {
						object.coordinates[i] = format.readGeometry(object.coordinates[i]).getCoordinates();
					}
				}
			} else if (object.type === 'MultiPolygon') {
				object.coordinates =_.map(object.coordinates, function (part) {
					return _.map(part, function (line) {
						if (typeof line === 'string') {
							return format.readGeometry(line).getCoordinates();
						} else return line
					});
				});
			}

			return object
		}
		recurseDecode(object)

        return opt_options.format.readGeometry(object, opt_options)
    }

    readProjection (source) {
        return this.readProjectionFromObject(this.getObject_(source))
    }

    writeGeometry_ (geometry, opt_options = {}) {
        opt_options || (opt_options = {})

        if (opt_options.format == null) {
            throw new Error('undefined geometry format')
        }

        return opt_options.format.writeGeometry(geometry, opt_options)
    }

    readGeometryFromObject (object, opt_options = {}) {
        opt_options || (opt_options = {})
        opt_options.format || (opt_options.format = this.geometryFormat_)
        return this.readGeometry_(/** @type {GeoJSONGeometry} */ (object), opt_options)
    }

    readProjectionFromObject (object) {
        return GlonassJSON.PROJECTION
    }

    writeFeature (feature, opt_options = {}) {
        return JSON.stringify(this.writeFeatureObject(feature, opt_options))
    }

    writeFeatureObject (feature, opt_options = {}) {
        opt_options = this.adaptOptions(opt_options)
        opt_options.format = this.geometryFormat_

        let object = {}

        let id = feature.getId()
        if (id != null) {
            object[this.dataPrimaryKeyName_] = id
        }

        let geometry = feature.getGeometry()

        if (geometry != null) {
            object[this.dataGeometryName_] = this.writeGeometry_(geometry, opt_options)
        }

        let properties = feature.getProperties()
        delete properties[feature.getGeometryName()]

        if (Object.keys(properties).length) {
            for (let prop in properties) {
                if (properties.hasOwnProperty(prop)) {
                    object[prop] = properties[prop]
                }
            }
        }

        return object
    }

    writeFeatures (features, opt_options = {}) {
        return JSON.stringify(this.writeFeaturesObject(features, opt_options))
    }

    writeFeaturesObject (features, opt_options = {}) {
        opt_options = this.adaptOptions(opt_options)
        let objects = [], i = 0, l = features.length
        while (i < l) {
            objects.push(this.writeFeatureObject(features[i], opt_options))
            ++i
        }
        return objects
    }

    writeGeometry (geometry, opt_options = {}) {
        return JSON.stringify(this.writeGeometryObject(geometry, opt_options))
    }

    writeGeometryObject (geometry, opt_options = {}) {
        opt_options = this.adaptOptions(opt_options)
        opt_options.format = this.geometryFormat_
        return this.writeGeometry_(geometry, opt_options)
    }
}
